import { useEffect, useState } from "react";
import { Heading } from "react-aria-components";

import { Button } from "../../button";
import { Modal } from "../../modal";
import { Tab, TabList, TabPanel, Tabs } from "../../tabs";
import { TextField } from "../../text-field";
import { Uploader } from "../../upload-image/uploader";

interface ImageModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onInsert: (src: string, alt: string) => void;
}

export function ImageModal({
  isOpen,
  onOpenChange,
  onInsert,
}: ImageModalProps) {
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setUrl("");
      setAlt("");
      setError(null);
    }
  }, [isOpen]);

  const handleUrlSubmit = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Please enter an image URL");
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      setError("URL must start with http:// or https://");
      return;
    }
    onInsert(trimmed, alt.trim());
    onOpenChange(false);
  };

  const handleUploadSuccess = (src: string) => {
    onInsert(src, alt.trim());
    onOpenChange(false);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
      <div className="p-6">
        <Heading className="mb-4 text-lg font-semibold" slot="title">
          Insert Image
        </Heading>
        <div className="mb-4">
          <TextField
            label="Alt Text"
            value={alt}
            onChange={setAlt}
            placeholder="Describe the image (optional)"
          />
        </div>
        <Tabs>
          <TabList>
            <Tab id="upload">Upload</Tab>
            <Tab id="url">URL</Tab>
          </TabList>
          <TabPanel id="upload" className="pt-4">
            <Uploader
              bucket="articles"
              prefix="help"
              onUploadSuccess={handleUploadSuccess}
              onCancel={() => onOpenChange(false)}
            />
          </TabPanel>
          <TabPanel id="url" className="pt-4">
            <div className="space-y-4">
              <TextField
                label="Image URL"
                value={url}
                onChange={(value) => {
                  setUrl(value);
                  setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleUrlSubmit();
                  }
                }}
                placeholder="https://example.com/image.jpg"
                autoFocus
                errorMessage={error ?? undefined}
                isInvalid={!!error}
              />
              <div className="flex justify-end gap-2">
                <Button onPress={() => onOpenChange(false)}>Cancel</Button>
                <Button
                  color="primary"
                  onPress={handleUrlSubmit}
                  isDisabled={!url.trim()}
                >
                  Insert
                </Button>
              </div>
            </div>
          </TabPanel>
        </Tabs>
      </div>
    </Modal>
  );
}
